import React from 'react';
import { useEditor } from '@tiptap/react';

import { TIPTAP_EXTENSIONS } from './constants';
import { getClipboardImageFile, insertImageFileAsBase64, updateSelectedNodeIndentation } from './helpers';

type UseEntryTiptapParams = {
  value?: string;
  onChange: (value: string) => void;
};

export const useEntryTiptap = ({ value, onChange }: UseEntryTiptapParams) => {
  const editor = useEditor({
    immediatelyRender: false,
    extensions: TIPTAP_EXTENSIONS,
    content: value || '<p></p>',
    editorProps: {
      handlePaste: (_view, event) => {
        const file = getClipboardImageFile(event);

        if (!file) {
          return false;
        }

        event.preventDefault();
        insertImageFileAsBase64(editor, file);
        return true;
      }
    },
    onUpdate: ({ editor: currentEditor }) => {
      onChange(currentEditor.getHTML());
    }
  });

  React.useEffect(() => {
    if (!editor) {
      return;
    }

    const nextValue = value || '<p></p>';
    const currentValue = editor.getHTML();

    if (nextValue !== currentValue) {
      editor.commands.setContent(nextValue, { emitUpdate: false });
    }
  }, [value, editor]);

  const promptForLink = () => {
    if (!editor) {
      return;
    }

    const currentHref = editor.getAttributes('link').href || '';
    const currentTarget = editor.getAttributes('link').target || '_blank';
    const currentRel = editor.getAttributes('link').rel || 'noopener noreferrer nofollow';
    const href = window.prompt('Enter URL', currentHref);

    if (href === null) {
      return;
    }

    if (!href.trim()) {
      editor.chain().focus().unsetLink().run();
      return;
    }

    editor.chain().focus().setLink({ href, target: currentTarget, rel: currentRel }).run();
  };

  const promptForImage = () => {
    if (!editor) {
      return;
    }

    const src = window.prompt('Enter image URL');

    if (!src) {
      return;
    }

    editor.chain().focus().setImage({ src }).run();
  };

  const onImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    insertImageFileAsBase64(editor, file, () => {
      event.target.value = '';
    });
  };

  const onIndent = () => {
    updateSelectedNodeIndentation(editor, 'indent');
  };

  const onOutdent = () => {
    updateSelectedNodeIndentation(editor, 'outdent');
  };

  return {
    editor,
    onImageFileChange,
    onIndent,
    onOutdent,
    promptForImage,
    promptForLink
  };
};
